import * as React from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { SortByShape } from '../../../hooks/use-sort-data'
import { SortDirection } from '../../../types'
import { DateFilter } from '../components/DateFilter'
import { OptionsTable } from '../components/OptionsTable'
import { StickyShadow } from '../components/StickyShadow'
import { StrikeFilter } from '../components/StrikeFilter'
import ViewFooter from '../components/ViewFooter'
import { columnMap } from '../constants/columns'
import { useOptionsQuery } from '../hooks/useOptionsQuery'
import { useOptionsSortChange } from '../hooks/useOptionsSortChange'
import { useOptionsSortOrder } from '../hooks/useOptionsSortOrder'
import {
  ColumnId,
  ColumnRendererProps,
  OptionType,
  OptionsByDateLoader,
  OptionsByStrikeLoader,
  OptionsSubpage,
  OptionsView,
  RowKind,
  TableColumnDefinition,
} from '../types'
import { evalLinkOrder, getViewLink } from '../utils/query'

const LEFT_COLUMNS = [ColumnId.ContractName, ColumnId.LastTrade]
const RIGHT_PRICES_COLUMNS = [
  ColumnId.LastClose,
  ColumnId.BidPrice,
  ColumnId.AskPrice,
  ColumnId.ChangeUsd,
  ColumnId.ChangePct,
  ColumnId.Volume,
  ColumnId.OpenInterest,
]

const RIGHT_GREEK_COLUMNS = [
  ColumnId.LastClose,
  ColumnId.IV,
  ColumnId.Delta,
  ColumnId.Gamma,
  ColumnId.Theta,
  ColumnId.Vega,
  ColumnId.Rho,
]

const BY_DATE_PRICES_COLUMNS = [...LEFT_COLUMNS, ColumnId.StrikePrice, ...RIGHT_PRICES_COLUMNS].map(
  (id) => columnMap[id]
)
const BY_STRIKE_PRICES_COLUMNS = [...LEFT_COLUMNS, ColumnId.ExpirationDate, ...RIGHT_PRICES_COLUMNS].map(
  (id) => columnMap[id]
)

const BY_DATE_GREEK_COLUMNS = [...LEFT_COLUMNS, ColumnId.StrikePrice, ...RIGHT_GREEK_COLUMNS].map((id) => columnMap[id])
const BY_STRIKE_GREEK_COLUMNS = [...LEFT_COLUMNS, ColumnId.ExpirationDate, ...RIGHT_GREEK_COLUMNS].map(
  (id) => columnMap[id]
)

function getColumnRenderer(searchParams: URLSearchParams) {
  return (column: TableColumnDefinition, props: ColumnRendererProps) => {
    switch (column.id) {
      case ColumnId.StrikePrice:
      case ColumnId.ExpirationDate:
        const middleLink = getViewLink(column.id, searchParams, props.row)

        return (
          <Link to={`?${middleLink}`} className="tab-link">
            {column.renderColumn(props)}
          </Link>
        )
      default:
        return column.renderColumn(props)
    }
  }
}

function getColumns(subpage: OptionsSubpage, isByDateList: boolean): TableColumnDefinition[] {
  if (subpage === OptionsSubpage.Volatility) {
    return isByDateList ? BY_DATE_GREEK_COLUMNS : BY_STRIKE_GREEK_COLUMNS
  } else {
    return isByDateList ? BY_DATE_PRICES_COLUMNS : BY_STRIKE_PRICES_COLUMNS
  }
}

function removePrefix(sortOrder: string) {
  if (!sortOrder.includes('_')) return sortOrder
  const [prefix, rest] = sortOrder.split('_', 2)
  return prefix.startsWith('-') ? `-${rest}` : rest
}

export function ListRoute(props: OptionsByDateLoader | OptionsByStrikeLoader) {
  const [searchParams] = useSearchParams()
  const subpage = props.subpage
  const columnRenderer = React.useMemo(() => getColumnRenderer(searchParams), [searchParams])
  const isByDateList = props.view === OptionsView.ListByDate
  const columns = getColumns(subpage, isByDateList)
  const linkColumn = isByDateList ? ColumnId.StrikePrice : ColumnId.ExpirationDate
  const filterColumn = isByDateList ? ColumnId.ExpirationDate : ColumnId.StrikePrice
  const query = useOptionsQuery(searchParams)
  const prefixedTableOrder = useOptionsSortOrder(props.subpage, query, linkColumn)
  const tableOrder = removePrefix(prefixedTableOrder)
  const effectiveTableOrder = evalLinkOrder(tableOrder, linkColumn)
  const optionsSortChange = useOptionsSortChange(subpage, query)

  const handleSortChange = (
    column: TableColumnDefinition,
    sort: SortByShape<TableColumnDefinition>,
    changeSort: (newSort: any, direction?: SortDirection) => void,
    sortPrefix: string
  ) => {
    optionsSortChange(column, sort, changeSort, column.id === linkColumn ? '' : sortPrefix, column.id === linkColumn)
  }

  return (
    <>
      <div>
        <div className="sticky z-10 h-11 bg-primary shadow-md sticky-22"></div>
        <div className="sticky z-30 -mt-11 bg-primary py-2 sticky-22">
          <div className="fv-container flex items-center space-x-1">
            <div className="w-full text-right text-2xs text-muted-2">{columnMap[filterColumn].name}</div>
            <div className="w-28 shrink-0">
              {props.view === OptionsView.ListByDate && (
                <DateFilter currentExpiry={props.currentExpiry} expiries={props.expiries} />
              )}
              {props.view === OptionsView.ListByStrike && (
                <StrikeFilter currentStrike={props.currentStrike} strikes={props.strikes} />
              )}
            </div>
            <div className="w-full" />
          </div>
        </div>

        <div>
          <StickyShadow className="z-10 h-5.5 bg-primary sticky-33" />
          <div className="fv-container relative -mt-5.5">
            <div className="pointer-events-none sticky z-40 -mb-5.5 h-5.5 sticky-33">
              <h2 className="pointer-events-auto absolute -top-11 my-0 pt-2.5 text-base text-muted font-bold">Calls</h2>
            </div>
            <OptionsTable
              key={props.view}
              isHeaderSticky
              headerClass="sticky-33 z-10"
              ticker={props.ticker}
              lastClose={props.lastClose}
              lastTime={props.lastTime}
              columns={columns}
              kind={RowKind.Call}
              options={props.options.filter((option) => option.type === OptionType.Call)}
              renderColumn={columnRenderer}
              tableOrder={effectiveTableOrder}
              onSortChange={(column, sort, changeSort) => handleSortChange(column, sort, changeSort, 'call_')}
            />
          </div>
        </div>

        <div>
          <h2 className="fv-container sticky z-40 mt-7 h-11 items-center pt-2.5 text-left text-base text-muted sticky-22 font-bold">
            Puts
          </h2>
          <StickyShadow className="z-10 h-5.5 bg-primary sticky-33" />
          <div className="fv-container -mt-5.5">
            <OptionsTable
              key={props.view}
              isHeaderSticky
              headerClass="sticky-33 z-10"
              ticker={props.ticker}
              lastClose={props.lastClose}
              lastTime={props.lastTime}
              columns={columns}
              kind={RowKind.Put}
              options={props.options.filter((option) => option.type === OptionType.Put)}
              renderColumn={columnRenderer}
              tableOrder={effectiveTableOrder}
              onSortChange={(column, sort, changeSort) => handleSortChange(column, sort, changeSort, 'put_')}
            />
          </div>
        </div>
      </div>
      <ViewFooter className="fv-container pb-4" />
    </>
  )
}
